
import { useState, useEffect } from "react";

// Toast types
export type ToastProps = {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  variant?: "default" | "destructive";
  duration?: number;
};

// Maintain a global toast list to ensure consistency across components
let toasts: ToastProps[] = [];
let listeners: Array<(toasts: ToastProps[]) => void> = [];

// Notify all listeners when toasts change
const publish = () => {
  listeners.forEach((listener) => {
    listener([...toasts]);
  });
};

export function toast(props: Omit<ToastProps, "id">) {
  const id = Math.random().toString(36).slice(2, 10);
  const newToast = {
    id,
    ...props,
    duration: props.duration || 5000, // Default to 5 seconds
  };
  
  toasts = [...toasts, newToast];
  publish();

  // Auto-dismiss after duration
  setTimeout(() => {
    dismiss(id);
  }, newToast.duration);

  return {
    id,
    dismiss: () => dismiss(id),
    update: (props: Omit<ToastProps, "id">) => update(id, props),
  };
}

export function update(id: string, props: Omit<ToastProps, "id">) {
  toasts = toasts.map((toast) => (toast.id === id ? { ...toast, ...props } : toast));
  publish();
}

export function dismiss(id: string) {
  toasts = toasts.filter((toast) => toast.id !== id);
  publish();
}

export function useToast() {
  const [localToasts, setLocalToasts] = useState<ToastProps[]>(toasts);

  useEffect(() => {
    const listener = (updatedToasts: ToastProps[]) => {
      setLocalToasts(updatedToasts);
    };

    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  return {
    toasts: localToasts,
    toast,
    dismiss,
    update,
  };
}
